import {
  Button,
  ButtonGroup,
  Chip,
  DotsHorizontalIcon,
  Dropdown,
  DropdownMenu,
  DropdownMenuItem,
  EditIcon,
  EmptyState,
  InfoIcon,
  LockIcon,
  Tooltip,
  UnlockIcon,
} from '@bp/ui-components';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useConfirm } from '../../../hooks/useConfirm';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  useDeleteTimetableVersionMutation,
  useDuplicateTimetableVersionMutation,
  useSetTimetableVersionEditableMutation,
  useUpdateTimetableVersionMutation,
} from '../../../types/planung-graphql-client-defs';
import { blockIfEventJobRunning, createEventsForDateRange } from '../../../utils/eventsApi';
import { downloadAscExport } from '../../../utils/timetable/timetableVersion';
import {
  showSuccessDeleteToast,
  showSuccessDuplicateToast,
  showSuccessSaveToast,
  showUserErrorToast,
} from '../../../utils/toast';
import { useTimetables } from '../../Timetable/hooks/useTimetables';
import { TimetableVersionModalForm } from '../Form/TimetableVersionModalForm';
import { buildVersionTree } from '../utils/buildVersionTree';
import { TimetableDefaultVersionListItem } from './TimetableDefaultVersionListItem';
import styles from './TimetableVersionsOverview.module.scss';
import { useValidCopyTargets } from './useValidCopyTargets';
import { VersionTreeCell } from './VersionTreeCell';

export type TimetableVersion = {
  uuid: string;
  active: boolean;
  version: number;
  updatedAt: string;
  description: string;
  status: number;
  parentVersion: string;
  parentUuid: string | null;
  isSnapshot: boolean;
  editable: boolean;
  originalUuid: string | null;
};

type TimetableVersionsOverviewProps = {
  timetableUuid: string | null;
  onCopyToNewTimetable: (version: TimetableVersion) => void;
  onNavigate: () => void;
  setLoading: (loading: boolean) => void;
};

export const TimetableVersionsOverview: FC<TimetableVersionsOverviewProps> = ({
  timetableUuid,
  onCopyToNewTimetable,
  onNavigate,
  setLoading,
}) => {
  const navigate = useNavigate();
  const permissions = usePermissionChecker();

  const { t } = useTranslation();
  const context = useMemorizedCacheTag('TIMETABLE_VERSIONS');

  const { confirm, ConfirmationDialog } = useConfirm();
  const { pimAuthClaims } = useAuthClaims();
  const schoolYear = useSelectedSchoolYear();

  const [versionToEdit, setVersionToEdit] = useState<TimetableVersion | null>(null);

  const [, updateVersion] = useUpdateTimetableVersionMutation();
  const [, setVersionEditable] = useSetTimetableVersionEditableMutation();
  const [, deleteVersion] = useDeleteTimetableVersionMutation();
  const [, duplicateVersion] = useDuplicateTimetableVersionMutation();

  // alle Pläne: die Übersicht wird auch für Pläne anderer Schuljahre geöffnet
  const { timetables: allTimetables, refetch: reexecuteDraftQuery } = useTimetables(undefined, { all: true });

  const timetable = allTimetables?.find((t) => t.uuid === timetableUuid);

  const duplicateVersionContext = useMemo(() => {
    return {
      additionalTypenames: ['TimetableVersion', 'TimetableCard', 'LessonClass'],
    };
  }, []);

  const getValidCopyTargets = useValidCopyTargets(timetable ?? null);

  const memoizedData: TimetableVersion[] = useMemo(() => {
    const versions = [...(timetable?.versions ?? [])];

    versions.sort((a, b) => {
      const aDate = a.updatedAt ? dayjs(a.updatedAt) : dayjs(a.createdAt);
      const bDate = b.updatedAt ? dayjs(b.updatedAt) : dayjs(b.createdAt);
      return aDate.isSameOrBefore(bDate) ? 1 : -1;
    });

    return versions.map((version) => {
      const parentVersion = versions.find((v) => v.uuid === version.parentUuid);

      return {
        uuid: version.uuid,
        active: version.active ?? false,
        parentVersion: parentVersion ? parentVersion.version.toString() : '',
        parentUuid: version.parentUuid ?? null,
        version: version.version,
        updatedAt: version.updatedAt
          ? dayjs(version.updatedAt).format('DD.MM.YYYY - HH:mm')
          : dayjs(version.createdAt).format('DD.MM.YYYY - HH:mm'),
        description: version.description ?? '',
        isSnapshot: version.isSnapshot ?? false,
        editable: version.editable ?? false,
        originalUuid: version.originalUuid ?? null,
        status:
          !version.placedCardsCount || version.cardsCount === 0
            ? 0
            : Math.floor((version.placedCardsCount / version.cardsCount) * 100),
      };
    });
  }, [timetable]);

  const activeVersion = memoizedData.find((v) => v.active);

  const blockPhase = useMemo(() => {
    const blocks = timetable?.timetableBlocks ?? [];
    if (blocks.length === 0) {
      return 'none';
    }
    const today = dayjs();
    if (blocks.some((b) => dayjs(b.start).isSameOrBefore(today, 'day') && dayjs(b.end).isSameOrAfter(today, 'day'))) {
      return 'running' as const;
    }
    if (blocks.some((b) => dayjs(b.start).isAfter(today, 'day'))) {
      return 'future' as const;
    }
    return 'past' as const;
  }, [timetable]);

  const hasStartedBlock = (timetable?.timetableBlocks.length ?? 0) > 0 && blockPhase !== 'future';

  const nonSnapshotVersions = memoizedData.filter((t) => !t.isSnapshot);

  // Baum aller Versionen; die aktive Version sitzt an ihrer natürlichen Stelle und wird hervorgehoben.
  const versionTree = buildVersionTree(nonSnapshotVersions);

  const snapshotVersions = memoizedData.filter((t) => t.isSnapshot);

  function onDataNavigate(uuid: string) {
    onNavigate();
    navigate(
      `${t('routes.versions.slug')}/${uuid}/${t('routes.versionPages.data.slug')}/${t('routes.versionsFilter.class.slug')}`,
    );
  }

  function onBoardNavigate(uuid: string) {
    onNavigate();
    navigate(`${t('routes.versions.slug')}/${uuid}/${t('routes.versionPages.board.slug')}`);
  }

  async function onSave(editVersion: Partial<TimetableVersion>) {
    if (!editVersion || !editVersion.uuid) return;

    // Die letzte aktive Version eines einem Abschnitt zugeordneten Stundenplans darf nicht
    // deaktiviert werden. Das Aktivieren einer anderen Version (editVersion.active === true)
    // bleibt erlaubt und deaktiviert die bisherige Version weiter automatisch.
    const timetableInBlock = (timetable?.timetableBlocks?.length ?? 0) > 0;
    const deactivatingActiveVersion = editVersion.active === false && activeVersion?.uuid === editVersion.uuid;
    if (timetableInBlock && deactivatingActiveVersion) {
      showUserErrorToast({ text: t('timetableVersion.cannotDeactivateLastInBlock'), error: undefined });
      return;
    }

    if (await blockIfEventJobRunning()) return;
    setLoading(true);

    // Beim Aktivieren deaktiviert das Backend die bisher aktive Version im selben Schritt.
    const current = memoizedData.find((v) => v.uuid === editVersion.uuid);
    const result = await updateVersion(
      {
        uuid: editVersion.uuid,
        input: {
          active: editVersion.active ?? current?.active ?? false,
          description: editVersion.description ?? null,
        },
      },
      context,
    );
    if (!result || result.error) {
      showUserErrorToast({ text: t('common.errorToastText'), error: result.error });
    } else {
      showSuccessSaveToast();
      if (editVersion.active === true) {
        const nonPastBlocks = (timetable?.timetableBlocks ?? []).filter((b) =>
          dayjs(b.end).isSameOrAfter(dayjs(), 'day'),
        );
        if (nonPastBlocks.length > 0 && schoolYear) {
          const from = nonPastBlocks.reduce(
            (min, b) => (dayjs(b.start).isBefore(dayjs(min)) ? b.start : min),
            nonPastBlocks[0].start,
          );
          const to = nonPastBlocks.reduce(
            (max, b) => (dayjs(b.end).isAfter(dayjs(max)) ? b.end : max),
            nonPastBlocks[0].end,
          );
          await createEventsForDateRange({
            organizationUuid: pimAuthClaims.getOrganizationUuid(),
            schoolYearUuid: schoolYear.uuid,
            from: dayjs(from).format('YYYY-MM-DD'),
            to: dayjs(to).format('YYYY-MM-DD'),
          });
        }
      }
    }
    setLoading(false);
    setVersionToEdit(null);
  }

  const onDuplicate = async (version: TimetableVersion, createNewTimetable: boolean) => {
    // Ein neuer Stundenplan braucht erst seine Stammdaten (und ggf. die Zeitraster-Zuordnung).
    if (createNewTimetable) {
      onCopyToNewTimetable(version);
      return;
    }
    setLoading(true);
    const result = await duplicateVersion(
      {
        timetableVersionUuid: version.uuid,
        options: {
          createNewTimetable: createNewTimetable,
        },
        organizationUuid: pimAuthClaims.getOrganizationUuid(),
      },
      duplicateVersionContext,
    );
    if (!result || result.error) {
      showUserErrorToast({ error: result.error });
    } else {
      showSuccessDuplicateToast();
      // Bei einer Duplikation in denselben Stundenplan reicht die Cache-Invalidierung nicht
      // zuverlässig aus (z. B. wenn es bisher nur die eine aktive Version gab) – Refetch erzwingen,
      // damit die neue Version ohne Reload im Overlay erscheint.
      if (!createNewTimetable) {
        reexecuteDraftQuery({ requestPolicy: 'network-only' });
      }
    }
    setLoading(false);
  };

  // Derselbe aSc-Export wie im Stecktafel-Menü — hier ohne Klassen-Einschränkung, weil die
  // Übersicht keinen Klassen-Scope kennt: leer ⇒ ganze Version.
  const onExport = async (version: TimetableVersion) => {
    await downloadAscExport(version.uuid, []);
  };

  const lockVersion = async (version: TimetableVersion) => {
    if (await blockIfEventJobRunning()) return;
    setLoading(true);
    await setVersionEditable({ uuid: version.uuid, editable: false }, context);
    const nonPastBlocks = (timetable?.timetableBlocks ?? []).filter((b) => dayjs(b.end).isSameOrAfter(dayjs(), 'day'));
    if (nonPastBlocks.length > 0 && schoolYear) {
      const from = nonPastBlocks.reduce(
        (min, b) => (dayjs(b.start).isBefore(dayjs(min)) ? b.start : min),
        nonPastBlocks[0].start,
      );
      const to = nonPastBlocks.reduce(
        (max, b) => (dayjs(b.end).isAfter(dayjs(max)) ? b.end : max),
        nonPastBlocks[0].end,
      );
      await createEventsForDateRange({
        organizationUuid: pimAuthClaims.getOrganizationUuid(),
        schoolYearUuid: schoolYear.uuid,
        from: dayjs(from).format('YYYY-MM-DD'),
        to: dayjs(to).format('YYYY-MM-DD'),
      });
    }
    setLoading(false);
  };

  const fixVersion = async (version: TimetableVersion) => {
    if (await blockIfEventJobRunning()) return;
    const res = await confirm({
      title: t('timetableVersion.attention'),
      message: t('timetableVersion.fixVersionMessage'),
      confirmText: t('timetableVersion.fixVersion'),
      cancelText: t('common.cancel'),
    });
    if (res) {
      setLoading(true);

      await setVersionEditable({ uuid: version.uuid, editable: true }, context);

      const { data, error } = await duplicateVersion(
        {
          timetableVersionUuid: version.uuid,
          options: {
            createNewTimetable: false,
            snapshot: true,
          },
          organizationUuid: pimAuthClaims.getOrganizationUuid(),
        },
        duplicateVersionContext,
      );
      setLoading(false);
      if (!data || error) {
        showUserErrorToast({ error });
      } else {
        onDataNavigate(version.uuid);
      }
      setLoading(false);
    }
  };

  const onCopy = async (targetUuid: string, versionsUuids: string[]) => {
    setLoading(true);
    for (const versionUuid of versionsUuids) {
      const { data, error } = await duplicateVersion(
        {
          timetableVersionUuid: versionUuid,
          options: {
            createNewTimetable: false,
            targetTimetableUuid: targetUuid,
          },
          organizationUuid: pimAuthClaims.getOrganizationUuid(),
        },
        duplicateVersionContext,
      );
      if (!data || error) {
        showUserErrorToast({ error });
      } else {
        showSuccessDuplicateToast();
      }
    }
    setLoading(false);
  };

  async function onDelete(version: TimetableVersion) {
    const confirmed = await confirm({
      title: t('timetableVersion.deleteTitle', { version: version.version }),
      message: t('timetableVersion.deleteConfirmMessage'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
    });
    if (!confirmed) return;
    setLoading(true);

    // Karten, Abhaengigkeiten, Sperren und versionseigene Zuordnungen raeumt der Resolver in einer
    // Transaktion mit ab.
    const result = await deleteVersion(
      { timetableVersionUuid: version.uuid, organizationUuid: pimAuthClaims.getOrganizationUuid() },
      context,
    );

    if (result.error || !result.data?.deleteTimetableVersion.nodesDeleted) {
      showUserErrorToast({ text: t('timetableVersion.deleteFailed'), error: result.error });
    } else {
      showSuccessDeleteToast();
    }
    setLoading(false);
  }

  async function onActivate(version: TimetableVersion) {
    if (!timetable?.draft && !activeVersion && hasStartedBlock) {
      const confirmed = await confirm({
        title: t('timetableVersion.attention'),
        message: t('timetableVersion.activateStartedTimetableMessage'),
        confirmText: t('common.activate'),
        cancelText: t('common.cancel'),
      });
      if (!confirmed) return;
    }
    if (await blockIfEventJobRunning()) return;
    setLoading(true);

    // Die bisher aktive Version deaktiviert das Backend im selben Schritt.
    await updateVersion(
      { uuid: version.uuid, input: { active: true, description: version.description ?? null } },
      context,
    );
    const nonPastBlocks = (timetable?.timetableBlocks ?? []).filter((b) => dayjs(b.end).isSameOrAfter(dayjs(), 'day'));
    if (nonPastBlocks.length > 0 && schoolYear) {
      const from = nonPastBlocks.reduce(
        (min, b) => (dayjs(b.start).isBefore(dayjs(min)) ? b.start : min),
        nonPastBlocks[0].start,
      );
      const to = nonPastBlocks.reduce(
        (max, b) => (dayjs(b.end).isAfter(dayjs(max)) ? b.end : max),
        nonPastBlocks[0].end,
      );
      await createEventsForDateRange({
        organizationUuid: pimAuthClaims.getOrganizationUuid(),
        schoolYearUuid: schoolYear.uuid,
        from: dayjs(from).format('YYYY-MM-DD'),
        to: dayjs(to).format('YYYY-MM-DD'),
      });
    }
    setLoading(false);
  }

  async function onDeactivate(version: TimetableVersion) {
    // Die letzte aktive Version eines einem Abschnitt zugeordneten Stundenplans darf nicht
    // deaktiviert werden (es bliebe sonst keine aktive Version übrig).
    if ((timetable?.timetableBlocks?.length ?? 0) > 0) {
      showUserErrorToast({ text: t('timetableVersion.cannotDeactivateLastInBlock'), error: undefined });
      return;
    }
    if (await blockIfEventJobRunning()) return;
    setLoading(true);
    const result = await updateVersion(
      { uuid: version.uuid, input: { active: false, description: version.description ?? null } },
      context,
    );
    if (!result || result.error) {
      showUserErrorToast({ text: t('common.errorToastText'), error: result.error });
    } else {
      showSuccessSaveToast();
    }
    setLoading(false);
  }

  function handleClose() {
    setLoading(false);
    setVersionToEdit(null);
  }

  const targets = activeVersion ? getValidCopyTargets() : [];

  const editable = activeVersion?.editable ?? true;

  const activeVersionMenu: DropdownMenuItem[] | null = activeVersion
    ? [
        {
          label: t('common.duplicate'),
          onClick: () => onDuplicate(activeVersion, false),
        },
        {
          label: t('common.copyTo'),
          subContent: [
            {
              label: t('timetableVersion.copyToNewTimetable'),
              onClick: () => onDuplicate(activeVersion, true),
            },
            { type: 'ruler' },
            ...targets.map((target) => ({
              label: target.name,
              onClick: () => onCopy(target.uuid, [activeVersion.uuid]),
            })),
          ],
        },
        {
          label: t('common.export'),
          onClick: () => onExport(activeVersion),
        },
        { type: 'ruler' },
        {
          label: t('common.deactivate'),
          onClick: () => onDeactivate(activeVersion),
          disabled: !timetable?.draft || (timetable?.timetableBlocks?.length ?? 0) > 0,
        },
        { type: 'ruler' },
        {
          label: t('timetableVersion.snapshots'),
          disabled: snapshotVersions.length === 0,
          subContent: [
            ...snapshotVersions
              .filter((v) => v.originalUuid === activeVersion.uuid)
              .map((version) => ({
                label: version.updatedAt,
                onClick: () => onDataNavigate(version.uuid),
              })),
          ],
        },
      ]
    : null;

  const hasBlocks = (timetable?.timetableBlocks.length ?? 0) > 0;

  if (hasBlocks && editable && activeVersion && !timetable?.draft) {
    activeVersionMenu?.push({
      type: 'default',
      color: 'error',
      label:
        blockPhase === 'future'
          ? t('timetableVersion.createEvents')
          : blockPhase === 'running'
            ? t('timetableVersion.completeFixAndCreateEvents')
            : t('timetableVersion.completeFix'),
      onClick: () => lockVersion(activeVersion),
    });
  } else if (hasBlocks && activeVersion && !timetable?.draft) {
    activeVersionMenu?.push({
      type: 'default',
      color: 'error',
      label: blockPhase === 'future' ? t('timetableVersion.createEvents') : t('timetableVersion.fixVersion'),
      onClick: () => fixVersion(activeVersion),
    });
  }

  const renderActiveRow = (version: TimetableVersion, prefix: boolean[], hasChildren: boolean) => {
    if (!activeVersionMenu) return null;
    return (
      <div key={version.uuid} className={classNames(styles.data, styles['data-active'])}>
        <VersionTreeCell prefix={prefix} hasChildren={hasChildren}>
          {version.version}
        </VersionTreeCell>
        <div className={styles.edited}>{version.updatedAt}</div>
        <div className={classNames(styles.name, styles['name-with-chip'])}>
          <Chip
            className={styles['active-badge']}
            value={t('common.active.full')}
            bgColor='var(--color-primary)'
            color='var(--color-white)'
            isSmall
          />
          <span className={styles['name-text']}>{version.description}</span>
        </div>
        <div className={styles.status}>
          {`${version.status}%`}
          {editable && !timetable?.draft ? (
            <Tooltip triggerClass={styles.lock} content={t('timetable.lockedEditable')}>
              <UnlockIcon />
            </Tooltip>
          ) : (
            <>
              {!permissions?.canWriteTimetable() ||
                (!timetable?.draft && (
                  <Tooltip triggerClass={styles.lock} content={t('timetable.locked')}>
                    <LockIcon />
                  </Tooltip>
                ))}
            </>
          )}
        </div>
        <div className={styles.actions}>
          <Button hierarchy='tertiary' onClick={() => onDataNavigate(version.uuid)}>
            {t('timetable.timetableData')}
          </Button>
          <Button hierarchy='tertiary' className='mr-3' onClick={() => onBoardNavigate(version.uuid)}>
            {t('pinboard.title', { count: 1 })}
          </Button>
          <ButtonGroup disabled={!permissions?.canWriteTimetable()}>
            <Button
              icon={<EditIcon className='small' />}
              hierarchy='tertiary'
              onClick={() => setVersionToEdit(version)}
            />
            <Dropdown
              usePortal={false}
              trigger={<Button icon={<DotsHorizontalIcon className='small' />} hierarchy='tertiary' />}
            >
              <DropdownMenu data={activeVersionMenu} usePortal={false} />
            </Dropdown>
          </ButtonGroup>
        </div>
      </div>
    );
  };

  return (
    <div className={styles['versions-overview']}>
      {activeVersion?.editable && hasStartedBlock && (
        <div className={styles['editable-hint']}>
          <InfoIcon color='var(--color-warning)' />
          {t('timetableVersion.editableVersionHint')}
        </div>
      )}
      <div className={styles.versions}>
        <div className={styles.hint}>{t('timetableVersion.title', { count: nonSnapshotVersions.length })}</div>
        <div className={styles.list}>
          <div className={styles.header}>
            <div className={styles.version}>{t('timetableVersion.title', { count: 1 })}</div>
            <div className={styles.edited}>{t('common.updatedAt')}</div>
            <div className={styles.name}>{t('common.name')}</div>
            <div className={styles.status}>{t('common.status')}</div>
            <div className={styles.actions}></div>
          </div>
          {nonSnapshotVersions.length === 0 ? (
            <EmptyState
              title={t('timetableVersion.noActiveVersion')}
              subtitle={t('timetableVersion.noPastVersionsHint')}
              size='small'
              forcedHeight='100px'
              hideIcon
            />
          ) : (
            <div className={classNames(styles['data-wrapper'])}>
              {versionTree.map(({ version, prefix, hasChildren }) =>
                version.active ? (
                  renderActiveRow(version, prefix, hasChildren)
                ) : (
                  <TimetableDefaultVersionListItem
                    readonly={!permissions?.canWriteTimetable()}
                    key={version.uuid}
                    version={version}
                    treePrefix={prefix}
                    hasChildren={hasChildren}
                    timetable={timetable ?? null}
                    hasActiveVersion={!!activeVersion}
                    onBoardNavigate={onBoardNavigate}
                    onCopy={onCopy}
                    onDataNavigate={onDataNavigate}
                    onDelete={onDelete}
                    onDuplicate={onDuplicate}
                    onExport={onExport}
                    onEdit={setVersionToEdit}
                    onActivate={onActivate}
                  />
                ),
              )}
            </div>
          )}
        </div>
      </div>
      <TimetableVersionModalForm
        editVersion={versionToEdit}
        loading={false}
        onSave={onSave}
        onClose={handleClose}
        timeTableIsActive={!timetable?.draft}
        timetableInBlock={(timetable?.timetableBlocks?.length ?? 0) > 0}
      />

      <ConfirmationDialog />
    </div>
  );
};
